import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail, Phone } from "lucide-react";
import Reveal from "./Reveal";
import { COUNTRIES } from "../lib/countries";
import { waLink, mailLink, CONTACT, openExternal } from "../lib/contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCES = [
  { id: "school", label: "School" },
  { id: "college", label: "College" },
  { id: "corporate", label: "Corporate" },
  { id: "individual", label: "Individual" },
  { id: "partner", label: "Partner" },
];

const INTERESTS = [
  { id: "certification", label: "A certification" },
  { id: "innovation_arena", label: "The Innovation Arena" },
  { id: "adoption_services", label: "AI Adoption Services (Audit / Build / Transformation)" },
  { id: "talent_hiring", label: "AI Talent Hiring (post a role / sponsor the Arena / build a custom cohort)" },
  { id: "partner_program", label: "Partner program" },
  { id: "custom", label: "Custom program" },
  { id: "not_sure", label: "Not sure — guide me" },
];

const initial = {
  name: "",
  email: "",
  phone: "",
  country: "",
  organization: "",
  audience: "",
  interest: "",
  message: "",
};

export default function EnquiryForm() {
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k, v) => {
    setData((d) => ({ ...d, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  // Listen for prefill events from other CTAs (e.g., ReachUs band, Audiences cards, Adoption Services)
  useEffect(() => {
    const onPrefill = (e) => {
      const d = e?.detail || {};
      if (!d.audience && !d.interest) return;
      setData((prev) => ({
        ...prev,
        ...(d.audience ? { audience: d.audience } : {}),
        ...(d.interest ? { interest: d.interest } : {}),
      }));
      setSubmitted(false);
    };
    window.addEventListener("enquiry:prefill", onPrefill);
    return () => window.removeEventListener("enquiry:prefill", onPrefill);
  }, []);

  const validate = () => {
    const e = {};
    if (!data.name.trim()) e.name = "Required";
    if (!/^\S+@\S+\.\S+$/.test(data.email)) e.email = "Valid email required";
    if (data.phone.replace(/\D/g, "").length < 10) e.phone = "Min 10 digits";
    if (!data.country) e.country = "Select a country";
    if (!data.audience) e.audience = "Select one";
    if (!data.interest) e.interest = "Select one";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: data.name.trim(),
        email: data.email.trim(),
        phone: data.phone.trim(),
        country: data.country,
        organization: data.organization.trim() || null,
        audience: data.audience,
        interest: data.interest,
        message: data.message.trim() || null,
      };
      const res = await axios.post(`${API}/enquiries`, payload);
      if (res.status === 201 || res.status === 200) {
        setSubmitted(true);
        toast.success("Thank you. We'll respond within 48 hours.");
        setData(initial);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="enquiry"
      className="py-10 md:py-14 border-t hairline"
      style={{ background: "var(--canvas)" }}
      data-testid="enquiry-section"
    >
      <div className="container-edge">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="section-number">13 — ENQUIRE</p>
            </Reveal>
            <Reveal>
              <h2
                className="mt-6 font-display text-[40px] sm:text-[52px] lg:text-[68px] leading-[0.95] tracking-tight"
                style={{ fontWeight: 500 }}
              >
                Every program is scoped to <em className="italic grad-brand-text" style={{ fontWeight: 500 }}>your context.</em>
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <p
                className="mt-6 text-[16px] md:text-[18px] leading-relaxed max-w-[460px]"
                style={{ color: "var(--ink-secondary)" }}
              >
                School. College. Corporate. Individual. Every audience, every certification, every
                Innovation Arena edition gets a custom-scoped proposal in 48 hours. No public
                pricing — because no two contexts are the same.
              </p>
            </Reveal>
            <Reveal delay={200}>
              <div
                className="mt-10 pt-6 border-t hairline font-mono text-[12px] leading-relaxed"
                style={{ color: "var(--ink-tertiary)", letterSpacing: "0.04em" }}
              >
                Institutional enquiries route to leadership directly.
                <br />
                Individual enquiries → admissions team.
                <br />
                Partner enquiries → partnerships team.
                <br />
                Adoption Services enquiries → enterprise team.
                <br />
                AI Talent Hiring enquiries → talent partnerships team.
              </div>
            </Reveal>

            <Reveal delay={260}>
              <div className="mt-10" data-testid="enquiry-direct-channels">
                <p className="mono-label-gold mb-4">PREFER A QUICK MESSAGE?</p>
                <div className="flex flex-col gap-2.5">
                  <a
                    href={waLink("Hi AI For Everyone — I'd like to talk about AI training.")}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={openExternal}
                    className="group inline-flex items-center justify-between gap-3 px-4 py-3 rounded-sm border transition-all hover:-translate-y-0.5"
                    style={{ borderColor: "var(--border-strong)", background: "white" }}
                    data-testid="enquiry-whatsapp"
                  >
                    <span className="inline-flex items-center gap-3 text-[14px]" style={{ color: "var(--ink)" }}>
                      <span
                        className="inline-flex w-8 h-8 items-center justify-center rounded-sm"
                        style={{ background: "rgba(37,211,102,0.12)", color: "#25D366" }}
                      >
                        <MessageCircle size={16} strokeWidth={1.75} />
                      </span>
                      WhatsApp
                    </span>
                    <span className="font-mono text-[11px]" style={{ color: "var(--ink-tertiary)", letterSpacing: "0.06em" }}>
                      {CONTACT.phoneDisplay} →
                    </span>
                  </a>
                  <a
                    href={mailLink({ subject: "Enquiry — AI For Everyone", body: "Hi, I'd like to know more about your programs." })}
                    className="group inline-flex items-center justify-between gap-3 px-4 py-3 rounded-sm border transition-all hover:-translate-y-0.5"
                    style={{ borderColor: "var(--border-strong)", background: "white" }}
                    data-testid="enquiry-email"
                  >
                    <span className="inline-flex items-center gap-3 text-[14px]" style={{ color: "var(--ink)" }}>
                      <span
                        className="inline-flex w-8 h-8 items-center justify-center rounded-sm"
                        style={{ background: "rgba(79,70,229,0.10)", color: "var(--accent-primary)" }}
                      >
                        <Mail size={16} strokeWidth={1.75} />
                      </span>
                      Email
                    </span>
                    <span className="font-mono text-[11px]" style={{ color: "var(--ink-tertiary)", letterSpacing: "0.06em" }}>
                      {CONTACT.email} →
                    </span>
                  </a>
                  <a
                    href={`tel:+${CONTACT.whatsappNumber}`}
                    className="group inline-flex items-center justify-between gap-3 px-4 py-3 rounded-sm border transition-all hover:-translate-y-0.5"
                    style={{ borderColor: "var(--border-strong)", background: "white" }}
                    data-testid="enquiry-call"
                  >
                    <span className="inline-flex items-center gap-3 text-[14px]" style={{ color: "var(--ink)" }}>
                      <span
                        className="inline-flex w-8 h-8 items-center justify-center rounded-sm"
                        style={{ background: "rgba(201,169,97,0.16)", color: "var(--accent-gold)" }}
                      >
                        <Phone size={16} strokeWidth={1.75} />
                      </span>
                      Call us
                    </span>
                    <span className="font-mono text-[11px]" style={{ color: "var(--ink-tertiary)", letterSpacing: "0.06em" }}>
                      {CONTACT.phoneDisplay} →
                    </span>
                  </a>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <Reveal>
                <div
                  className="p-12 border hairline-strong"
                  style={{ background: "white" }}
                  data-testid="enquiry-success"
                >
                  <p className="mono-label-gold">RECEIVED</p>
                  <h3 className="mt-3 font-display text-[36px] tracking-tight" style={{ fontWeight: 500 }}>
                    Thank you.
                  </h3>
                  <p className="mt-4 text-[16px]" style={{ color: "var(--ink-secondary)" }}>
                    We've received your enquiry. A confirmation has been sent to your inbox. Our
                    team will be in touch within 48 hours with a proposal scoped to your context.
                  </p>
                  <button
                    type="button"
                    className="mt-8 link-underline text-[14px] text-ink"
                    onClick={() => setSubmitted(false)}
                    data-testid="enquiry-reset"
                  >
                    Submit another enquiry →
                  </button>
                </div>
              </Reveal>
            ) : (
              <Reveal>
                <form
                  onSubmit={onSubmit}
                  className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8"
                  noValidate
                  data-testid="enquiry-form"
                >
                  {/* Name */}
                  <div className="md:col-span-1">
                    <label className="form-label" htmlFor="f-name">Name *</label>
                    <input
                      id="f-name"
                      type="text"
                      className="form-input"
                      value={data.name}
                      onChange={(e) => update("name", e.target.value)}
                      autoComplete="name"
                      required
                      data-testid="form-name"
                    />
                    {errors.name && <p className="mt-1 text-[12px] text-red-600">{errors.name}</p>}
                  </div>

                  {/* Email */}
                  <div className="md:col-span-1">
                    <label className="form-label" htmlFor="f-email">Email *</label>
                    <input
                      id="f-email"
                      type="email"
                      className="form-input"
                      value={data.email}
                      onChange={(e) => update("email", e.target.value)}
                      autoComplete="email"
                      required
                      data-testid="form-email"
                    />
                    {errors.email && <p className="mt-1 text-[12px] text-red-600">{errors.email}</p>}
                  </div>

                  {/* Phone */}
                  <div className="md:col-span-1">
                    <label className="form-label" htmlFor="f-phone">Phone / WhatsApp *</label>
                    <input
                      id="f-phone"
                      type="tel"
                      className="form-input"
                      placeholder="+1 555 0123"
                      value={data.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      autoComplete="tel"
                      required
                      data-testid="form-phone"
                    />
                    {errors.phone && <p className="mt-1 text-[12px] text-red-600">{errors.phone}</p>}
                  </div>

                  {/* Country */}
                  <div className="md:col-span-1">
                    <label className="form-label" htmlFor="f-country">Country *</label>
                    <select
                      id="f-country"
                      className="form-input bg-transparent"
                      value={data.country}
                      onChange={(e) => update("country", e.target.value)}
                      required
                      data-testid="form-country"
                    >
                      <option value="">Select country</option>
                      {COUNTRIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    {errors.country && <p className="mt-1 text-[12px] text-red-600">{errors.country}</p>}
                  </div>

                  {/* Organization */}
                  <div className="md:col-span-2">
                    <label className="form-label" htmlFor="f-org">Organization</label>
                    <input
                      id="f-org"
                      type="text"
                      className="form-input"
                      value={data.organization}
                      onChange={(e) => update("organization", e.target.value)}
                      autoComplete="organization"
                      data-testid="form-organization"
                    />
                  </div>

                  {/* Audience */}
                  <div className="md:col-span-2">
                    <p className="form-label">I am enquiring as *</p>
                    <div className="flex flex-wrap gap-2" data-testid="form-audience-group">
                      {AUDIENCES.map((a) => (
                        <button
                          type="button"
                          key={a.id}
                          onClick={() => update("audience", a.id)}
                          className={`filter-pill ${data.audience === a.id ? "is-active" : ""}`}
                          aria-pressed={data.audience === a.id}
                          data-testid={`form-audience-${a.id}`}
                        >
                          {a.label}
                        </button>
                      ))}
                    </div>
                    {errors.audience && <p className="mt-2 text-[12px] text-red-600">{errors.audience}</p>}
                  </div>

                  {/* Interest */}
                  <div className="md:col-span-2">
                    <p className="form-label">I'm interested in *</p>
                    <div className="flex flex-wrap gap-2" data-testid="form-interest-group">
                      {INTERESTS.map((a) => (
                        <button
                          type="button"
                          key={a.id}
                          onClick={() => update("interest", a.id)}
                          className={`filter-pill ${data.interest === a.id ? "is-active" : ""}`}
                          aria-pressed={data.interest === a.id}
                          data-testid={`form-interest-${a.id}`}
                        >
                          {a.label}
                        </button>
                      ))}
                    </div>
                    {errors.interest && <p className="mt-2 text-[12px] text-red-600">{errors.interest}</p>}
                  </div>

                  {/* Message */}
                  <div className="md:col-span-2">
                    <label className="form-label" htmlFor="f-message">Tell us about your context (optional)</label>
                    <textarea
                      id="f-message"
                      rows={4}
                      className="form-input resize-none"
                      value={data.message}
                      onChange={(e) => update("message", e.target.value)}
                      data-testid="form-message"
                    />
                  </div>

                  <div className="md:col-span-2 pt-2">
                    <button
                      type="submit"
                      className="btn-primary disabled:opacity-60"
                      disabled={submitting}
                      data-testid="form-submit"
                    >
                      {submitting ? "Sending…" : "Send Enquiry — Receive Proposal in 48 Hours"}
                    </button>
                    <p
                      className="mt-4 font-mono text-[11px] leading-relaxed max-w-[560px]"
                      style={{ color: "var(--ink-tertiary)", letterSpacing: "0.04em" }}
                    >
                      By submitting, you agree to be contacted by AI For Everyone regarding your
                      enquiry. We do not sell or share your information.
                    </p>
                  </div>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
